import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  ScrollView,
  Platform,
  useWindowDimensions,
  Linking,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';

// React Native Reusables Components
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

// Services & Components
import {
  authAPI,
  billingAPI,
  getBillingCheckoutConflict,
  getPaywallOfferDecline,
  invalidateBillingCaches,
} from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import { phaseForAccessState } from '../../navigation/gatedAccess';
import { useAuth } from '@/context/AuthContext';
import { useAccessPolicy } from '../../hooks/useAccessPolicy';
import { hasEffectiveBillingAccess, shouldStartCardBackedTrial } from '../../utils/billingAccess';
import { syncBillingAfterMutation } from '../../utils/billingSync';
import { clearPlanContext, getPlanContext } from '../../utils/planContext';
import { isMentorProfile, resolvePostCheckoutRoute } from '../../utils/postCheckoutRoute';
import { mentorPlanNote, planCardEmphasis, recommendedTierFor } from '../../utils/planRecommendation';
import AppHeaderBar from '../../components/layout/AppHeaderBar';
import type { IntakeSummary, SchoolPlanPreview } from '@/types';
import {
  trackAuthEvent,
  recordPaywallView,
  recordCheckoutInitiated,
  recordPaywallLeftWithoutCheckout,
  recordComebackOfferShown,
  recordComebackOfferDismissed,
  recordComebackOfferDeclined,
  recordFinalOfferDismissed,
  recordFinalOfferDeclined,
  getPaywallOfferLadderState,
  isComebackOfferEligibleClientSide,
  isFinalOfferEligibleClientSide,
} from '../../services/analytics';
import { reportHandledError } from '../../services/errorReporting';
import EmbeddedCheckoutModal from '../../components/billing/EmbeddedCheckoutModal';
import ComebackOfferBanner from '../../components/billing/ComebackOfferBanner';
import FinalOfferBanner from '../../components/billing/FinalOfferBanner';
import { openCheckoutSession } from '../../utils/checkout';
import {
  claimCheckoutCompletion,
  readPendingCheckout,
  rememberPendingCheckout,
} from '../../utils/checkoutCompletion';
import { confirmDialog, showDialog } from '../../utils/dialogs';
import { describePlanChange, fallbackPlanChangeCopy, planChangeSummary } from '../profile/planChangePreview';
import { formatCurrencyAmount, formatDate, planDisplayName } from '../profile/profileFormat';
import { openExternalLink } from '../profile/profileLinks';
import {
  createCheckoutError,
  getCheckoutErrorDetails,
  markCheckoutErrorShown,
  reportCheckoutFailure,
  showCheckoutFailureDialog,
  type CheckoutFailureFeedback,
} from '../../utils/checkoutFailures';
import SampleReportViewer from '../../components/onboarding/SampleReportViewer';
import { brand } from '../../design-system/theme/tokens';

// Professional Design Tokens
const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#EF4444',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
  border: '#E5E7EB',
};

interface PlanSelectionScreenProps {
  onPlanSelected?: (planId: string) => void;
}

interface PlanFeature {
  text: string;
  included: boolean;
}

type BillingCycle = '1mo' | '3mo' | '6mo';
const CARD_TRIAL_DAYS = 3;
const CHECKOUT_SURFACE = 'plan_selection';

const DEFAULT_BILLING_CYCLE_BY_TIER: Record<string, BillingCycle> = {
  tier1: '1mo',
  tier2: '1mo',
  tier3: '3mo',
};

// Final-offer (step 2 of the paywall ladder) fixed first-month price, shown
// per the user's resolved checkout currency (pricing.currency, same source
// the plan cards already use). USD is the fallback for any other currency.
const FINAL_OFFER_FIXED_PRICE_BY_CURRENCY: Record<string, string> = {
  EUR: '€10',
  USD: '$10',
  TRY: '₺499',
};
// Only monthly tiers carry the final offer — never the Mentor Coach pack.
const FINAL_OFFER_ELIGIBLE_TIERS = ['tier1', 'tier2'] as const;

interface PlanInterval {
  cycle: BillingCycle;
  months: number;
  cents: number; // in selected currency
}

interface Plan {
  id: string;
  name: string;
  tagline?: string;
  intervals: PlanInterval[];
  currency: string;
  features: PlanFeature[];
  buttonText: string;
}

/**
 * Enhanced Plan Selection Screen - Professional Design System Implementation
 *
 * - Uses React Native Reusables for consistent professional UI
 * - Complete i18n support with no hardcoded text
 * - Proper safe area handling
 * - Professional design following style guide
 * - Multiple pricing tiers with per-tier interval selector (1/3/6 months)
 * - Stripe checkout integration
 * - Responsive layout for mobile/tablet/desktop
 */
export default function PlanSelectionScreenEnhanced({ onPlanSelected }: PlanSelectionScreenProps) {
  const router = useRouter();
  const { t, language } = useTranslation();
  const { user, billingStatus, refreshAuth } = useAuth();
  const insets = useSafeAreaInsets();
  const { width: screenWidth } = useWindowDimensions();

  const isMobile = screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const isWide = screenWidth >= 1280;
  // VIS-ONB-3: below 1024px plans stack one full-width card per row; the
  // reassurance card drops below the plans (in normal flow) below 1280px.
  const stackPlans = isMobile || isTablet;
  const stackSidebar = !isWide;
  const { isSchoolAdmin, isB2C, canAccessIndividualPaywall } = useAccessPolicy();

  // Main acquisition path is a card-upfront Stripe trial.
  const trialExpired = Boolean(billingStatus?.free_trial_expired);
  const isPendingCancellation = Boolean(billingStatus?.cancel_at_period_end);
  // Only genuinely unused trial eligibility starts a new card-backed trial.
  // Grandfathered no-card users keep access until their original expiry; if
  // they voluntarily subscribe early, Checkout charges now instead of trying
  // (and failing) to grant a second trial.
  const useCardTrialCheckout = Boolean(
    isB2C && shouldStartCardBackedTrial(billingStatus),
  );
  // Banner shows "No payment due today" while card-trial checkout is offered.
  // Once trial eligibility is consumed, selecting a plan pays today.
  const showNoPaymentBanner = useCardTrialCheckout;

  const [intakeSummary, setIntakeSummary] = useState<IntakeSummary | null>(null);
  // B2C reads the intake to pick the recommended plan; the screen-view event waits for it (F3).
  const [intakeSettled, setIntakeSettled] = useState(false);
  const recommendedTier = recommendedTierFor(intakeSummary);
  // Until the B2C intake answers, no card is badged (a mentor would see tier2 flash first).
  const badgeTier = isB2C && !intakeSettled ? null : recommendedTier;

  // Per-tier selected cycle. Tier1 only supports 1mo; tier3 only supports 3mo/6mo.
  const [selectedIntervals, setSelectedIntervals] = useState<Record<string, BillingCycle>>({
    tier1: '1mo',
    tier2: '1mo',
    tier3: '3mo',
  });
  // `selectedPlan` marks the tier whose checkout is in flight (drives the
  // "Processing" CTA) — it is NOT the landing-page preselection.
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  // Plan carried over from the landing deep link (stored plan context). The
  // intake now runs before this screen, so the context survives the tour and is
  // highlighted here instead of being consumed by an early checkout.
  const [preselectedTier, setPreselectedTier] = useState<string | null>(null);
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState<{
    currency: string;
    tiers: Record<string, {
      recommended?: boolean;
      intervals?: Array<{ cycle: BillingCycle; months: number; eur: { cents: number }; usd: { cents: number }; try: { cents: number } }>;
    }>;
  } | null>(null);
  const [pricingReady, setPricingReady] = useState(false);
  const [pricingError, setPricingError] = useState(false);
  const [pricingAttempt, setPricingAttempt] = useState(0);

  const [seatTarget, setSeatTarget] = useState<string>('');
  const [preview, setPreview] = useState<SchoolPlanPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [checkoutVisible, setCheckoutVisible] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<{ tier: 'tier1' | 'tier2' | 'tier3'; cycle: BillingCycle; cardTrial?: boolean; promo?: 'comeback' | 'final_offer' }>({ tier: 'tier1', cycle: '1mo' });

  // Paywall-abandonment offer ladder: step 1 "comeback" (30% off) → step 2
  // "final_offer" (fixed first-month price, paid, no trial). Strictly
  // escalating and never both visible at once — see the eligibility effect
  // below and analytics.ts's isComebackOfferEligibleClientSide /
  // isFinalOfferEligibleClientSide for the exact gating rules. This is a
  // client heuristic only: the backend independently re-validates
  // eligibility on every checkout request and can decline it (see
  // handlePlanSelect / the EmbeddedCheckoutModal `promo` prop for the
  // graceful-degradation retry).
  const [comebackOfferEligible, setComebackOfferEligible] = useState(false);
  const [comebackOfferArmed, setComebackOfferArmed] = useState(false);
  const [finalOfferEligible, setFinalOfferEligible] = useState(false);
  const [finalOfferArmed, setFinalOfferArmed] = useState(false);
  const comebackOfferShownTrackedRef = React.useRef(false);
  const finalOfferShownTrackedRef = React.useRef(false);
  // Tracks whether this visit ever started a checkout, so the unmount
  // cleanup below can tell "navigated away mid-checkout" (not abandonment)
  // apart from "left without ever trying" (the decline-moment trigger).
  const checkoutEverStartedThisVisitRef = React.useRef(false);
  const isB2CRef = React.useRef(isB2C);

  React.useEffect(() => {
    isB2CRef.current = isB2C;
  }, [isB2C]);

  React.useEffect(() => {
    recordPaywallView();
    // Unmount = navigated away in-app without ever starting checkout during
    // this visit. One of the two "decline moment" signals (the other is an
    // explicit checkout-modal dismiss, recorded where that modal closes).
    return () => {
      if (isB2CRef.current && !checkoutEverStartedThisVisitRef.current) {
        recordPaywallLeftWithoutCheckout();
      }
    };
  }, []);

  React.useEffect(() => {
    if (comebackOfferEligible && !comebackOfferShownTrackedRef.current) {
      comebackOfferShownTrackedRef.current = true;
      recordComebackOfferShown();
      trackAuthEvent('paywall_offer_shown', {
        offer_type: 'comeback_discount',
        trigger: 'paywall_abandonment',
        offer_step: 1,
      });
    }
  }, [comebackOfferEligible]);

  React.useEffect(() => {
    if (finalOfferEligible && !finalOfferShownTrackedRef.current) {
      finalOfferShownTrackedRef.current = true;
      trackAuthEvent('paywall_offer_shown', {
        offer_type: 'final_offer',
        trigger: 'paywall_abandonment',
        offer_step: 2,
      });
    }
  }, [finalOfferEligible]);

  const handleComebackOfferAccept = React.useCallback(() => {
    setComebackOfferArmed(true);
    trackAuthEvent('paywall_offer_accepted', {
      offer_type: 'comeback_discount',
      trigger: 'paywall_abandonment',
      offer_step: 1,
    });
  }, []);

  const handleComebackOfferDismiss = React.useCallback(() => {
    setComebackOfferEligible(false);
    recordComebackOfferDismissed();
    trackAuthEvent('paywall_offer_dismissed', {
      offer_type: 'comeback_discount',
      trigger: 'paywall_abandonment',
      offer_step: 1,
    });
  }, []);

  const handleFinalOfferAccept = React.useCallback(() => {
    setFinalOfferArmed(true);
    trackAuthEvent('paywall_offer_accepted', {
      offer_type: 'final_offer',
      trigger: 'paywall_abandonment',
      offer_step: 2,
    });
  }, []);

  const handleFinalOfferDismiss = React.useCallback(() => {
    setFinalOfferEligible(false);
    recordFinalOfferDismissed();
    trackAuthEvent('paywall_offer_dismissed', {
      offer_type: 'final_offer',
      trigger: 'paywall_abandonment',
      offer_step: 2,
    });
  }, []);

  // Preselect (highlight) the plan chosen on the landing page, if any.
  React.useEffect(() => {
    if (!isB2C) return;
    const context = getPlanContext();
    if (!context) return;
    setPreselectedTier(context.planTier);
    // The landing cycle (validated per tier in planContext) wins over the
    // tier default; checkout still resolves it against the live intervals.
    setSelectedIntervals((prev) => ({
      ...prev,
      [context.planTier]: context.cycle ?? prev[context.planTier] ?? DEFAULT_BILLING_CYCLE_BY_TIER[context.planTier] ?? '1mo',
    }));
  }, [isB2C]);

  // Defense in depth: a user who already has access can land here (e.g. after a
  // completed checkout that raced the phase refresh). Give them a way out
  // instead of a dead end. Never auto-redirect — changing plans is legitimate.
  const alreadyCovered = hasEffectiveBillingAccess(billingStatus, {
    requireBoundedB2CTrial: canAccessIndividualPaywall,
  }) || Boolean(billingStatus?.has_purchased_credits);

  // Neither offer applies to a user with any current/past subscription
  // (alreadyCovered), a user still on the card-trial acquisition path, or a
  // user whose trial already expired (a proxy for the backend's
  // never_subscribed check) — same gates for both ladder steps.
  const paywallOffersBlockedByBilling = alreadyCovered || useCardTrialCheckout || trialExpired;

  React.useEffect(() => {
    if (!isB2C || paywallOffersBlockedByBilling) {
      setComebackOfferEligible(false);
      setFinalOfferEligible(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const state = await getPaywallOfferLadderState();
      if (cancelled) return;
      setComebackOfferEligible(isComebackOfferEligibleClientSide(state));
      setFinalOfferEligible(isFinalOfferEligibleClientSide(state));
    })();
    return () => {
      cancelled = true;
    };
  }, [isB2C, paywallOffersBlockedByBilling]);

  React.useEffect(() => {
    if (!isB2C) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await authAPI.getIntakeSummary();
        if (!cancelled) setIntakeSummary(data);
      } catch {
        // Intake summary is optional UX enhancement — silently ignore failures.
      } finally {
        if (!cancelled) setIntakeSettled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isB2C]);

  const screenViewTrackedRef = React.useRef(false);
  React.useEffect(() => {
    if (screenViewTrackedRef.current || (isB2C && !intakeSettled)) return;
    screenViewTrackedRef.current = true;
    trackAuthEvent('plan_selection_screen_viewed', {
      recommended_tier: isB2C ? recommendedTier : null,
      recommendation_reason: isB2C && recommendedTier === 'tier3' ? 'mentor_intake' : 'default',
    });
  }, [isB2C, intakeSettled, recommendedTier]);

  const formatMoney = React.useCallback((amountCents: number, currencyCode?: string) => {
    const code = (currencyCode || 'USD').toUpperCase();
    try {
      const locale = code === 'TRY' ? 'tr-TR' : 'en-US';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(amountCents / 100);
    } catch {
      return `${code} ${(amountCents / 100).toFixed(2)}`;
    }
  }, []);

  const formatCents = React.useCallback((cents: number, currencyCode?: string) => {
    return formatMoney(cents, currencyCode);
  }, [formatMoney]);

  const loadPreview = React.useCallback(
    async (target?: number) => {
      if (!isSchoolAdmin || !user?.school_id) return;
      try {
        setPreviewError(null);
        setPreviewLoading(true);
        const data = await billingAPI.schoolPlanPreview(user.school_id, target);
        setPreview(data);
        if (typeof target !== 'number') {
          setSeatTarget(String(data.requested_seat_target ?? data.effective_seat_target));
        }
      } catch (error: any) {
        const message = error?.message || '';
        if (message.toLowerCase().includes('not configured')) {
          setPreviewError(t('plans.schoolPaywall.notConfigured'));
        } else {
          setPreviewError(message || t('plans.schoolPaywall.errorLoading'));
        }
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    },
    [isSchoolAdmin, user?.school_id, t]
  );

  React.useEffect(() => {
    if (!isSchoolAdmin || !user?.school_id) return;
    loadPreview();
  }, [isSchoolAdmin, user?.school_id, loadPreview]);

  // Load B2C pricing (TRY/EUR/USD) for display with a small settle delay to avoid flicker
  React.useEffect(() => {
    if (!isB2C) return;
    setPricingReady(false);
    setPricingError(false);
    (async () => {
      try {
        const start = Date.now();
        const p = await billingAPI.pricing();
        const tiersMap: Record<string, {
          recommended?: boolean;
          intervals?: Array<{ cycle: BillingCycle; months: number; eur: { cents: number }; usd: { cents: number }; try: { cents: number } }>;
        }> = {};
        for (const t of (p.tiers || [])) {
          tiersMap[t.tier] = {
            recommended: t.recommended,
            intervals: t.intervals as any,
          };
        }
        setPricing({ currency: p.currency || 'USD', tiers: tiersMap });
        const elapsed = Date.now() - start;
        if (elapsed < 350) {
          await new Promise((r) => setTimeout(r, 350 - elapsed));
        }
        setPricingReady(true);
      } catch {
        // VIS-ONB-2: say so and offer a retry instead of an empty price slot.
        setPricingError(true);
        setPricingReady(true);
      }
    })();
  }, [isB2C, pricingAttempt]);
  React.useEffect(() => {
    if (!isSchoolAdmin || !user?.school_id) return;
    if (!seatTarget) return;
    const parsed = parseInt(seatTarget, 10);
    if (Number.isNaN(parsed) || parsed <= 0) return;
    if (preview && parsed === (preview.requested_seat_target ?? preview.effective_seat_target)) return;
    const timer = setTimeout(() => {
      loadPreview(parsed);
    }, 400);
    return () => clearTimeout(timer);
  }, [seatTarget, isSchoolAdmin, user?.school_id, loadPreview, preview]);

  const handleSeatTargetChange = (value: string) => {
    const sanitized = value.replace(/\D/g, '');
    setSeatTarget(sanitized);
  };

  const handleSchoolCheckout = async () => {
    if (!isSchoolAdmin || !user?.school_id) return;
    const parsed = parseInt(seatTarget, 10);
    const seatCount = !Number.isNaN(parsed) && parsed > 0 ? parsed : preview?.effective_seat_target;
    if (!seatCount) {
      const message = t('plans.schoolPaywall.checkoutError');
      showDialog(message);
      return;
    }
    const totalCents = preview?.total_cents ?? null;
    const amount = typeof totalCents === 'number' ? totalCents / 100 : undefined;
    const currency = preview?.currency;
    try {
      setLoading(true);
      trackAuthEvent('checkout_initiated', {
        plan: 'b2b',
        plan_name: 'school',
        plan_display_name: t('plans.schoolPaywall.title'),
        seats: seatCount,
        amount,
        amount_cents: totalCents ?? undefined,
        currency,
      });
      const data = await billingAPI.schoolPlanCheckout(user.school_id, seatCount);
      const url = data.checkout_url;
      if (!url) {
        throw createCheckoutError('missing_checkout_url');
      }
      trackAuthEvent('checkout_session_created', {
        plan: 'b2b',
        plan_name: 'school',
        plan_display_name: t('plans.schoolPaywall.title'),
        seats: seatCount,
        amount,
        amount_cents: totalCents ?? undefined,
        currency,
      });
      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    } catch (error: any) {
      setLoading(false);
      await showCheckoutFailureDialog({
        error,
        surface: 'school_plan_selection',
        checkoutKey: `${user.school_id}:${seatCount}`,
        properties: {
          plan: 'b2b',
          plan_name: 'school',
          seats: seatCount,
          amount,
          amount_cents: totalCents ?? undefined,
          currency,
        },
        retry: handleSchoolCheckout,
        t,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (refreshingStatus || !isSchoolAdmin) return;
    try {
      setRefreshingStatus(true);
      await billingAPI.refresh();
      await refreshAuth();
    } catch (error: any) {
      const message = error?.message || t('plans.schoolPaywall.refreshError');
      showDialog(message);
    } finally {
      setRefreshingStatus(false);
    }
  };

  const renderSchoolPaywall = () => {
    const totalLabel = preview ? formatMoney(preview.total_cents, preview.currency) : null;
    return (
      <SafeAreaView className="flex-1 bg-surface">
        <AppHeaderBar />
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: 24,
            paddingTop: 40,
            paddingBottom: Math.max(insets.bottom + 40, 60),
          }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View className="w-full max-w-3xl mx-auto gap-6">
            <Card>
              <CardContent className="p-8 gap-4">
                <MaterialIcons name="school" size={40} color={COLORS.primary} />
                <Text className="text-2xl font-bold" style={{ color: COLORS.text.primary }}>
                  {t('plans.schoolPaywall.title')}
                </Text>
                <Text className="text-base leading-6" style={{ color: COLORS.text.secondary }}>
                  {t('plans.schoolPaywall.description')}
                </Text>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-8 gap-6">
                <Text className="text-xl font-semibold" style={{ color: COLORS.text.primary }}>
                  {t('plans.schoolPaywall.summaryTitle')}
                </Text>
                {previewLoading ? (
                  <View className="items-center py-6 gap-3">
                    <ActivityIndicator size="large" color={COLORS.primary} />
                    <Text className="text-base" style={{ color: COLORS.text.secondary }}>
                      {t('plans.schoolPaywall.loading')}
                    </Text>
                  </View>
                ) : previewError ? (
                  <View className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <Text className="text-sm" style={{ color: COLORS.error }}>
                      {previewError}
                    </Text>
                  </View>
                ) : preview ? (
                  <View className="gap-4">
                    <View className="gap-2">
                      <Text className="text-sm font-semibold uppercase tracking-wide" style={{ color: COLORS.text.muted }}>
                        {t('plans.schoolPaywall.baseLabel')}
                      </Text>
                      <Text className="text-base" style={{ color: COLORS.text.primary }}>
                        {t('plans.schoolPaywall.baseSummary', {
                          price: formatMoney(preview.base_price_cents, preview.currency),
                          seats: preview.base_seats,
                        })}
                      </Text>
                    </View>
                    <View className="gap-2">
                      <Text className="text-sm font-semibold uppercase tracking-wide" style={{ color: COLORS.text.muted }}>
                        {t('plans.schoolPaywall.addonLabel')}
                      </Text>
                      <Text className="text-base" style={{ color: COLORS.text.primary }}>
                        {`${t('plans.schoolPaywall.addonSummary', {
                          price: formatMoney(preview.addon_price_cents, preview.currency),
                          seats: preview.addon_package_size,
                        })} (${preview.addon_packages} ${preview.addon_packages === 1 ? t('plans.schoolPaywall.packageSingular') : t('plans.schoolPaywall.packagePlural')})`}
                      </Text>
                    </View>

                    <View className="gap-2">
                      <Text className="text-sm font-semibold" style={{ color: COLORS.text.secondary }}>
                        {t('plans.schoolPaywall.seatTargetLabel')}
                      </Text>
                      <TextInput
                        value={seatTarget}
                        onChangeText={handleSeatTargetChange}
                        keyboardType="number-pad"
                        placeholder={t('plans.schoolPaywall.seatTargetPlaceholder')}
                        className="border border-gray-200 rounded-lg px-4 py-3 text-base bg-white text-black"
                        placeholderTextColor={COLORS.text.muted}
                      />
                      <Text className="text-xs" style={{ color: COLORS.text.muted }}>
                        {t('plans.schoolPaywall.seatTargetHelp')}
                      </Text>
                    </View>

                    <View className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
                      <Text className="text-sm font-semibold" style={{ color: COLORS.primary }}>
                        {t('plans.schoolPaywall.monthlyTotal', {
                          price: totalLabel || formatMoney(preview.total_cents, preview.currency),
                        })}
                      </Text>
                    </View>
                  </View>
                ) : null}

                <View className="gap-2">
                  <Button
                    variant="default"
                    onPress={handleSchoolCheckout}
                    disabled={loading || previewLoading || Boolean(previewError)}
                    className="flex-row items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <ActivityIndicator size="small" color="#ffffff" />
                        <Text className="text-sm font-semibold text-white">
                          {t('plans.processing')}
                        </Text>
                      </>
                    ) : (
                      <>
                        <MaterialIcons name="payment" size={18} color="#ffffff" />
                        <Text className="text-sm font-semibold text-white">
                          {t('plans.schoolPaywall.checkout')}
                        </Text>
                      </>
                    )}
                  </Button>
                  <Text className="text-xs" style={{ color: COLORS.text.muted }}>
                    {t('plans.schoolPaywall.checkoutDescription')}
                  </Text>
                </View>

                <View className="gap-2">
                  <Text className="text-xs" style={{ color: COLORS.text.secondary }}>
                    {t('plans.schoolPaywall.instructions')}
                  </Text>
                  <Button
                    variant="outline"
                    onPress={handleRefreshStatus}
                    disabled={refreshingStatus}
                    className="flex-row items-center justify-center gap-2"
                  >
                    {refreshingStatus ? (
                      <>
                        <ActivityIndicator size="small" color={COLORS.primary} />
                        <Text className="text-sm font-semibold" style={{ color: COLORS.primary }}>
                          {t('common.loading')}
                        </Text>
                      </>
                    ) : (
                      <>
                        <MaterialIcons name="refresh" size={18} color={COLORS.primary} />
                        <Text className="text-sm font-semibold" style={{ color: COLORS.primary }}>
                          {t('plans.schoolPaywall.refresh')}
                        </Text>
                      </>
                    )}
                  </Button>
                </View>
              </CardContent>
            </Card>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  };

  // Build intervals for each tier from API response, with sensible fallback cents.
  const buildIntervals = (tierId: string, fallbackCycles: BillingCycle[]): PlanInterval[] => {
    const apiTier = pricing?.tiers[tierId];
    const currency = (pricing?.currency || 'USD').toLowerCase() as 'eur' | 'usd' | 'try';
    if (apiTier?.intervals && apiTier.intervals.length > 0) {
      return apiTier.intervals.map((iv) => ({
        cycle: iv.cycle,
        months: iv.months,
        cents: iv[currency]?.cents ?? iv.usd?.cents ?? 0,
      }));
    }
    // Fallback: return empty intervals so the card still renders
    return fallbackCycles.map((cycle) => ({
      cycle,
      months: cycle === '1mo' ? 1 : cycle === '3mo' ? 3 : 6,
      cents: 0,
    }));
  };

  // Memoized: a fresh array every render made handleCheckoutSessionCreated
  // (and, through it, the embedded checkout modal's fetch effect) change
  // identity on every re-render (Oct 2026 checkout 409 incident).
  // buildIntervals only reads `pricing`.
  const plans: Plan[] = React.useMemo(() => [
    {
      id: 'tier1',
      name: t('plans.individual.name'),
      tagline: t('plans.individual.tagline'),
      intervals: buildIntervals('tier1', ['1mo']),
      currency: pricing?.currency || t('plans.currency'),
      features: [
        { text: t('plans.individual.features.transcripts'), included: true },
        { text: t('plans.individual.features.transcriptionMinutes'), included: true },
        { text: t('plans.individual.features.chats'), included: true },
        { text: t('plans.individual.features.analytics'), included: true },
        { text: t('plans.individual.features.support'), included: true },
      ],
      buttonText: t('plans.getStarted'),
    },
    {
      id: 'tier2',
      name: t('plans.advanced.name'),
      tagline: t('plans.advanced.tagline'),
      intervals: buildIntervals('tier2', ['1mo']),
      currency: pricing?.currency || t('plans.currency'),
      features: [
        { text: t('plans.advanced.features.transcripts'), included: true },
        { text: t('plans.advanced.features.transcriptionMinutes'), included: true },
        { text: t('plans.advanced.features.chats'), included: true },
        { text: t('plans.advanced.features.analytics'), included: true },
        { text: t('plans.advanced.features.support'), included: true },
        { text: t('plans.advanced.features.tracking'), included: true },
        { text: t('plans.advanced.features.coaching'), included: true },
      ],
      buttonText: t('plans.getStarted'),
    },
    {
      id: 'tier3',
      name: t('plans.professional.name'),
      tagline: t('plans.professional.tagline'),
      intervals: buildIntervals('tier3', ['3mo', '6mo']),
      currency: pricing?.currency || t('plans.currency'),
      features: [
        { text: t('plans.professional.features.transcripts'), included: true },
        { text: t('plans.professional.features.transcriptionMinutes'), included: true },
        { text: t('plans.professional.features.chats'), included: true },
        { text: t('plans.professional.features.analytics'), included: true },
        { text: t('plans.professional.features.support'), included: true },
        { text: t('plans.professional.features.tracking'), included: true },
        { text: t('plans.professional.features.coaching'), included: true },
        { text: t('plans.professional.features.mentoring'), included: true },
      ],
      buttonText: t('plans.getStarted'),
    },
  ], [pricing, t]);

  // Final-offer pricing: fixed intro price per the resolved checkout
  // currency, plus each monthly tier's real full price for the mandatory
  // renewal-transparency copy ("Renews at <price>/month...").
  const finalOfferFixedPriceLabel =
    FINAL_OFFER_FIXED_PRICE_BY_CURRENCY[(pricing?.currency || 'USD').toUpperCase()]
    || FINAL_OFFER_FIXED_PRICE_BY_CURRENCY.USD;
  const finalOfferTiers = FINAL_OFFER_ELIGIBLE_TIERS.map((tierId) => {
    const tierPlan = plans.find((p) => p.id === tierId);
    const monthlyCents = tierPlan?.intervals.find((iv) => iv.cycle === '1mo')?.cents ?? 0;
    return {
      name: tierPlan?.name || tierId,
      fullPriceFormatted: formatMoney(monthlyCents, tierPlan?.currency),
    };
  });
  const finalOfferFullPriceByTier: Record<string, string> = Object.fromEntries(
    finalOfferTiers.map((tier, index) => [FINAL_OFFER_ELIGIBLE_TIERS[index], tier.fullPriceFormatted]),
  );

  const resolveBillingCycleForPlan = (plan: Plan | undefined, requestedCycle?: BillingCycle): BillingCycle => {
    const defaultCycle = (plan?.id && DEFAULT_BILLING_CYCLE_BY_TIER[plan.id]) || plan?.intervals[0]?.cycle || '1mo';
    if (!plan?.intervals.length) return defaultCycle;
    const requestedIsAllowed = Boolean(requestedCycle && plan.intervals.some((iv) => iv.cycle === requestedCycle));
    if (requestedIsAllowed) return requestedCycle!;
    const defaultIsAllowed = plan.intervals.some((iv) => iv.cycle === defaultCycle);
    return defaultIsAllowed ? defaultCycle : plan.intervals[0].cycle;
  };

  const showBillingAlert = (titleKey: string, messageKey: string) => {
    showDialog(t(titleKey), t(messageKey));
  };

  const confirmBillingAction = (
    titleKey: string,
    messageKey: string,
    confirmKey: string,
    detail?: string | null,
  ): Promise<boolean> => {
    const title = t(titleKey);
    const message = detail ? `${t(messageKey)}\n\n${detail}` : t(messageKey);
    const confirmLabel = t(confirmKey);
    return confirmDialog(title, message, confirmLabel, t('billing.conflicts.dismissAction'));
  };

  const resumeCurrentSubscription = async () => {
    await billingAPI.resumeSubscription();
    await syncBillingAfterMutation(refreshAuth);
    showBillingAlert('billing.conflicts.resumeSuccessTitle', 'billing.conflicts.resumeSuccessMessage');
    onPlanSelected?.('resume');
  };

  /**
   * BIL-L05: what the change costs today and when it applies, for the confirm dialog.
   * Best effort: without a preview the dialog shows generic copy (amount on the invoice).
   */
  const previewPlanChange = async (
    planId: string,
    cycle: BillingCycle,
  ): Promise<{ summary: string | null; prorationDate: number | null }> => {
    try {
      const preview = await billingAPI.changePlanPreview(planId as 'tier1' | 'tier2' | 'tier3', cycle);
      const copy = describePlanChange(preview, {
        t,
        formatMoney: (cents, currency) => formatCurrencyAmount(t, language, preview.currency, cents, currency),
        formatDate: (iso) => formatDate(iso || undefined),
        currentName: planDisplayName(t, preview.current_plan_tier),
        targetName: planDisplayName(t, preview.plan_tier),
        pendingName: preview.pending_plan_tier ? planDisplayName(t, preview.pending_plan_tier) : undefined,
      });
      return { summary: planChangeSummary(copy), prorationDate: preview.proration_date ?? null };
    } catch (previewError) {
      // Not blocking: confirm with generic copy and no proration date.
      trackAuthEvent('plan_change_preview_failed', { plan_tier: planId, billing_cycle: cycle });
      reportHandledError(previewError, {
        source: 'billing.plan_change_preview',
        event: 'plan_change_preview_failed',
        plan_tier: planId,
        billing_cycle: cycle,
      });
      return { summary: planChangeSummary(fallbackPlanChangeCopy(t)), prorationDate: null };
    }
  };

  const changeCurrentSubscription = async (planId: string, cycle: BillingCycle, prorationDate?: number | null) => {
    const response = await billingAPI.changePlan(
      planId as 'tier1' | 'tier2' | 'tier3',
      cycle,
      'always_invoice',
      prorationDate ?? null,
    );
    await syncBillingAfterMutation(refreshAuth);
    if (response.payment_pending) {
      // BIL-L03: the charge failed or needs authentication; the plan has not changed.
      if (response.invoice_url) {
        const payNow = await confirmDialog(
          t('profile.planChangePaymentPendingTitle'),
          t('profile.planChangePaymentPendingMessage'),
          t('profile.planChangePayInvoice'),
          t('billing.conflicts.dismissAction'),
        );
        if (payNow) {
          await openExternalLink(t, response.invoice_url, t('profile.planInvoiceLinkFailed'));
        }
      } else {
        showBillingAlert('profile.planChangePaymentPendingTitle', 'profile.planChangePaymentPendingMessage');
      }
      return;
    }
    showBillingAlert('billing.conflicts.changePlanSuccessTitle', 'billing.conflicts.changePlanSuccessMessage');
    onPlanSelected?.(planId);
  };

  const showPlanCheckoutError = async (
    error: unknown,
    planId: string,
    cycle: BillingCycle,
    retry: () => unknown | Promise<unknown>,
  ) => {
    await showCheckoutFailureDialog({
      error,
      surface: CHECKOUT_SURFACE,
      checkoutKey: `${planId}:${cycle}`,
      properties: {
        plan: planId,
        billing_period: cycle,
      },
      retry,
      t,
    });
  };

  const handleCheckoutConflict = async (
    error: any,
    planId: string,
    cycle: BillingCycle,
    initialFeedback?: CheckoutFailureFeedback,
  ): Promise<boolean> => {
    const conflict = error?.billingCheckoutConflict || getBillingCheckoutConflict(error);
    if (!conflict) return false;

    if (conflict.code === 'intro_offer_unavailable') {
      await billingAPI.refresh();
      await refreshAuth({ forceBillingRefresh: true });
      if (initialFeedback) {
        markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
          plan: planId,
          billing_period: cycle,
        });
      }
      showBillingAlert(
        'billing.conflicts.introOfferUnavailableTitle',
        'billing.conflicts.introOfferUnavailableMessage',
      );
      return true;
    }
    if (conflict.code === 'intro_offer_checkout_in_progress') {
      if (initialFeedback) {
        markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
          plan: planId,
          billing_period: cycle,
        });
      }
      trackAuthEvent('checkout_conflict_shown', {
        conflict_code: conflict.code,
        action: conflict.action || 'refresh',
        plan: planId,
        billing_period: cycle,
        surface: CHECKOUT_SURFACE,
      });
      showBillingAlert(
        'billing.conflicts.introCheckoutInProgressTitle',
        'billing.conflicts.introCheckoutInProgressMessage',
      );
      return true;
    }

    const retryOriginalCheckoutOnce = async (conflictCode: string): Promise<boolean> => {
      try {
        const checkoutResponse = await billingAPI.checkout(
          'b2c',
          planId as 'tier1' | 'tier2' | 'tier3',
          cycle,
          undefined,
          undefined,
          useCardTrialCheckout
            ? { startPaidTrial: true, trialPeriodDays: CARD_TRIAL_DAYS }
            : undefined,
        );
        if (!checkoutResponse.checkout_url) {
          throw createCheckoutError('missing_checkout_url');
        }
        await openCheckoutSession(checkoutResponse.checkout_url);
        await refreshAuth();
        trackAuthEvent('checkout_conflict_resolved', {
          conflict_code: conflictCode,
          resolution: 'checkout_retry',
          surface: CHECKOUT_SURFACE,
        });
        onPlanSelected?.(planId);
      } catch (retryError: any) {
        const retryConflict = getBillingCheckoutConflict(retryError);
        const diagnostics = getCheckoutErrorDetails(retryError);
        trackAuthEvent('checkout_conflict_resolution_failed', {
          conflict_code: retryConflict?.code || conflictCode,
          resolution: 'checkout_retry',
          error_code: diagnostics.errorCode,
          error_type: diagnostics.errorType,
          surface: CHECKOUT_SURFACE,
        });
        await showPlanCheckoutError(
          retryError,
          planId,
          cycle,
          () => retryOriginalCheckoutOnce(conflictCode),
        );
      }
      return true;
    };

    if (conflict.action === 'checkout') {
      return retryOriginalCheckoutOnce(conflict.code);
    }

    if (conflict.code === 'resume_available') {
      if (initialFeedback) {
        markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
          plan: planId,
          billing_period: cycle,
        });
      }
      trackAuthEvent('checkout_conflict_shown', {
        conflict_code: conflict.code,
        action: conflict.action || 'resume',
        plan: planId,
        billing_period: cycle,
        surface: CHECKOUT_SURFACE,
      });
      const confirmed = await confirmBillingAction(
        'billing.conflicts.resumeAvailableTitle',
        'billing.conflicts.resumeAvailableMessage',
        'billing.conflicts.resumeAction',
      );
      if (confirmed) {
        try {
          await resumeCurrentSubscription();
          trackAuthEvent('checkout_conflict_resolved', {
            conflict_code: conflict.code,
            resolution: 'resume',
            surface: CHECKOUT_SURFACE,
          });
        } catch (actionError: any) {
          const actionConflict = getBillingCheckoutConflict(actionError);
          if (actionConflict?.action === 'checkout') {
            return retryOriginalCheckoutOnce(actionConflict.code);
          }
          const diagnostics = getCheckoutErrorDetails(actionError);
          trackAuthEvent('checkout_conflict_resolution_failed', {
            conflict_code: conflict.code,
            resolution: 'resume',
            error_code: diagnostics.errorCode,
            error_type: diagnostics.errorType,
            surface: CHECKOUT_SURFACE,
          });
          await showPlanCheckoutError(actionError, planId, cycle, resumeCurrentSubscription);
        }
      } else {
        trackAuthEvent('checkout_conflict_resolved', {
          conflict_code: conflict.code,
          resolution: 'dismissed',
          surface: CHECKOUT_SURFACE,
        });
      }
      return true;
    }

    const titleKey = conflict.code === 'already_subscribed'
      ? 'billing.conflicts.alreadySubscribedTitle'
      : 'billing.conflicts.pendingCancellationTitle';
    const messageKey = conflict.code === 'already_subscribed'
      ? 'billing.conflicts.alreadySubscribedMessage'
      : 'billing.conflicts.pendingCancellationMessage';
    if (initialFeedback) {
      markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
        plan: planId,
        billing_period: cycle,
      });
    }
    trackAuthEvent('checkout_conflict_shown', {
      conflict_code: conflict.code,
      action: conflict.action || 'change_plan',
      plan: planId,
      billing_period: cycle,
      surface: CHECKOUT_SURFACE,
    });
    const planChangePreview = await previewPlanChange(planId, cycle);
    const confirmed = await confirmBillingAction(
      titleKey,
      messageKey,
      'billing.conflicts.changePlanAction',
      planChangePreview.summary,
    );
    if (confirmed) {
      try {
        await changeCurrentSubscription(planId, cycle, planChangePreview.prorationDate);
        trackAuthEvent('checkout_conflict_resolved', {
          conflict_code: conflict.code,
          resolution: 'change_plan',
          surface: CHECKOUT_SURFACE,
        });
      } catch (actionError: any) {
        const actionConflict = getBillingCheckoutConflict(actionError);
        if (actionConflict?.action === 'checkout') {
          return retryOriginalCheckoutOnce(actionConflict.code);
        }
        const diagnostics = getCheckoutErrorDetails(actionError);
        trackAuthEvent('checkout_conflict_resolution_failed', {
          conflict_code: conflict.code,
          resolution: 'change_plan',
          error_code: diagnostics.errorCode,
          error_type: diagnostics.errorType,
          surface: CHECKOUT_SURFACE,
        });
        await showPlanCheckoutError(
          actionError,
          planId,
          cycle,
          () => changeCurrentSubscription(planId, cycle),
        );
      }
    } else {
      trackAuthEvent('checkout_conflict_resolved', {
        conflict_code: conflict.code,
        resolution: 'dismissed',
        surface: CHECKOUT_SURFACE,
      });
    }
    return true;
  };

  const handlePlanSelect = async (planId: string) => {
    if (loading) return;

    setLoading(true);
    setSelectedPlan(planId);
    const plan = plans.find((p) => p.id === planId);
    const cycle = resolveBillingCycleForPlan(plan, selectedIntervals[planId]);
    if (selectedIntervals[planId] !== cycle) {
      setSelectedIntervals((prev) => ({ ...prev, [planId]: cycle }));
    }
    const intervalData = plan?.intervals.find((iv) => iv.cycle === cycle);
    const amountCents = intervalData?.cents ?? null;
    const amount = typeof amountCents === 'number' ? amountCents / 100 : undefined;

    trackAuthEvent('plan_selected', {
      plan: planId,
      plan_name: plan?.name,
      billing_period: cycle,
      amount,
      amount_cents: amountCents ?? undefined,
      price: amount,
      currency: plan?.currency,
    });
    trackAuthEvent('checkout_initiated', {
      plan: planId,
      plan_name: plan?.name,
      billing_period: cycle,
      amount,
      amount_cents: amountCents ?? undefined,
      price: amount,
      currency: plan?.currency,
    });
    recordCheckoutInitiated();
    checkoutEverStartedThisVisitRef.current = true;

    // Comeback discount only applies to a straight paid checkout (never the
    // card-upfront trial) — see billing_personal.py's `promo == "comeback"
    // and not payload.start_paid_trial` gate. The final offer is monthly
    // tiers only (Students / Chasing Credentials, i.e. tier1/tier2) and also
    // never rides along with the card trial — it's explicitly pay-now.
    const applyComebackPromo = comebackOfferArmed && !useCardTrialCheckout;
    const applyFinalOfferPromo =
      finalOfferArmed
      && !useCardTrialCheckout
      && (planId === 'tier1' || planId === 'tier2')
      && cycle === '1mo';
    const activePromo: 'comeback' | 'final_offer' | undefined = applyFinalOfferPromo
      ? 'final_offer'
      : applyComebackPromo
        ? 'comeback'
        : undefined;

    try {
      if (Platform.OS === 'web') {
        // Embedded checkout. During acquisition, this is a card-upfront Stripe
        // trial; after trial eligibility is consumed, it is regular checkout.
        setCheckoutPlan({
          tier: planId as 'tier1' | 'tier2' | 'tier3',
          cycle,
          cardTrial: useCardTrialCheckout,
          promo: activePromo,
        });
        setCheckoutVisible(true);
        return;
      }

      const checkoutOptions: { startPaidTrial?: boolean; trialPeriodDays?: number; promo?: 'comeback' | 'final_offer' } = useCardTrialCheckout
        ? { startPaidTrial: true, trialPeriodDays: CARD_TRIAL_DAYS }
        : activePromo
          ? { promo: activePromo }
          : {};

      let checkoutResponse;
      try {
        checkoutResponse = await billingAPI.checkout(
          'b2c',
          planId as 'tier1' | 'tier2' | 'tier3',
          cycle,
          undefined,
          undefined,
          checkoutOptions,
        );
      } catch (checkoutError: any) {
        // Paywall-offer decline: not a checkout failure — the offer just
        // didn't hold up server-side. Drop it and retry once at full price.
        const decline = activePromo ? getPaywallOfferDecline(checkoutError) : null;
        if (decline) {
          if (activePromo === 'final_offer') {
            recordFinalOfferDeclined();
          } else {
            recordComebackOfferDeclined();
          }
          trackAuthEvent('paywall_offer_declined', {
            offer_type: activePromo === 'final_offer' ? 'final_offer' : 'comeback_discount',
            trigger: 'paywall_abandonment',
            offer_step: activePromo === 'final_offer' ? 2 : 1,
            reason: decline.code,
            surface: CHECKOUT_SURFACE,
          });
          checkoutResponse = await billingAPI.checkout(
            'b2c',
            planId as 'tier1' | 'tier2' | 'tier3',
            cycle,
            undefined,
            undefined,
            useCardTrialCheckout ? { startPaidTrial: true, trialPeriodDays: CARD_TRIAL_DAYS } : undefined,
          );
        } else {
          throw checkoutError;
        }
      }

      if (!checkoutResponse.checkout_url) {
        throw createCheckoutError('missing_checkout_url');
      }
      trackAuthEvent('checkout_session_created', {
        plan: planId,
        plan_name: plan?.name,
        billing_period: cycle,
        checkout_session_id: checkoutResponse.session_id,
        amount,
        amount_cents: amountCents ?? undefined,
        price: amount,
        currency: checkoutResponse.currency || plan?.currency,
      });

      await openCheckoutSession(checkoutResponse.checkout_url);
      await refreshAuth();
      onPlanSelected?.(planId);
    } catch (error: any) {
      if (error.response?.status === 401) {
        const properties = {
          plan: planId,
          plan_name: plan?.name,
          billing_period: cycle,
          amount,
          amount_cents: amountCents ?? undefined,
          price: amount,
          currency: plan?.currency,
        };
        const feedback = reportCheckoutFailure({
          error,
          surface: CHECKOUT_SURFACE,
          checkoutKey: `${planId}:${cycle}`,
          properties,
          t,
        });
        markCheckoutErrorShown(feedback, CHECKOUT_SURFACE, properties);
        showBillingAlert('common.error', 'plans.errors.authRequired');
        router.push(ROUTES.PUBLIC_LOGIN);
      } else {
        const properties = {
          plan: planId,
          plan_name: plan?.name,
          billing_period: cycle,
          amount,
          amount_cents: amountCents ?? undefined,
          price: amount,
          currency: plan?.currency,
        };
        const feedback = reportCheckoutFailure({
          error,
          surface: CHECKOUT_SURFACE,
          checkoutKey: `${planId}:${cycle}`,
          properties,
          t,
        });
        const handled = await handleCheckoutConflict(error, planId, cycle, feedback);
        if (handled) return;
        setLoading(false);
        await showCheckoutFailureDialog({
          error,
          feedback,
          surface: CHECKOUT_SURFACE,
          checkoutKey: `${planId}:${cycle}`,
          properties,
          retry: () => handlePlanSelect(planId),
          t,
        });
      }
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleContactSales = () => {
    const subject = encodeURIComponent(t('plans.schools.emailSubject'));
    const body = encodeURIComponent(t('plans.schools.emailBody'));
    trackAuthEvent('plan_contact_sales_clicked', { destination: 'mailto:contact@mentorcoaching.ai' });
    Linking.openURL(`mailto:contact@mentorcoaching.ai?subject=${subject}&body=${body}`);
  };

  // EmbeddedCheckoutModal handlers MUST be referentially stable: the modal's
  // session-fetch effect depends (transitively) on these props, so inline
  // arrows here re-fired it on every parent re-render — duplicating checkout
  // POSTs and racing the backend's intro-offer reservation (Aug 31 incident).
  const handleCheckoutSessionCreated = React.useCallback(
    (session: { session_id?: string; currency?: string }) => {
      const plan = plans.find((p) => p.id === checkoutPlan.tier);
      const intervalData = plan?.intervals.find((iv) => iv.cycle === checkoutPlan.cycle);
      const amountCents = intervalData?.cents ?? null;
      const amount = typeof amountCents === 'number' ? amountCents / 100 : undefined;
      // Lets a full-page Stripe return (redirect-based payment methods)
      // report the plan and dedupe checkout_completed by session id.
      rememberPendingCheckout({
        session_id: session.session_id,
        plan: checkoutPlan.tier,
        billing_period: checkoutPlan.cycle,
      });
      trackAuthEvent('checkout_session_created', {
        plan: checkoutPlan.tier,
        plan_name: plan?.name,
        billing_period: checkoutPlan.cycle,
        checkout_session_id: session.session_id,
        amount,
        amount_cents: amountCents ?? undefined,
        price: amount,
        currency: session.currency || plan?.currency,
      });
    },
    [plans, checkoutPlan.tier, checkoutPlan.cycle],
  );

  const handleCheckoutConflictResolved = React.useCallback(async () => {
    setCheckoutVisible(false);
    await refreshAuth();
    onPlanSelected?.(checkoutPlan.tier);
  }, [refreshAuth, onPlanSelected, checkoutPlan.tier]);

  const handleCheckoutComplete = React.useCallback(async () => {
    const pendingSessionId = readPendingCheckout()?.session_id;
    if (claimCheckoutCompletion(pendingSessionId)) {
      trackAuthEvent('checkout_completed', {
        plan: checkoutPlan.tier,
        billing_period: checkoutPlan.cycle,
        completion_source: 'stripe_embedded_checkout',
        checkout_session_id: pendingSessionId,
      });
    }
    setCheckoutVisible(false);
    clearPlanContext();
    invalidateBillingCaches();
    await refreshAuth({ forceBillingRefresh: true });
    const freshStatus = await billingAPI.status();
    const nextPhase = phaseForAccessState(user, freshStatus);
    let summary = intakeSummary;
    if (!summary && nextPhase === 'authenticated') {
      try {
        summary = await authAPI.getIntakeSummary();
      } catch (error) {
        // Routing falls back to the no-summary path after checkout.
        reportHandledError(error, { source: 'checkout.post_checkout_intake_summary' });
      }
    }
    router.replace(resolvePostCheckoutRoute({
      phase: nextPhase,
      user,
      billingStatus: freshStatus,
      mentorProfile: isMentorProfile(summary),
    }) as any);
  }, [checkoutPlan.tier, checkoutPlan.cycle, intakeSummary, refreshAuth, router, user]);

  const handleCheckoutClose = React.useCallback(() => {
    // Explicit dismiss of the checkout modal without completing — the
    // other "decline moment" signal (see the unmount cleanup for the
    // in-app-navigation-away case). Makes the comeback offer show
    // immediately on the next arrival instead of waiting out 24h.
    recordPaywallLeftWithoutCheckout();
    setCheckoutVisible(false);
  }, []);

  if (isSchoolAdmin) {
    return renderSchoolPaywall();
  }

  if (!isB2C) {
    return (
      <SafeAreaView className="flex-1 bg-surface">
        <AppHeaderBar />
        <View
          style={{
            flex: 1,
            paddingHorizontal: 32,
            paddingTop: 40,
            paddingBottom: Math.max(insets.bottom + 40, 60),
            maxWidth: 700,
            alignSelf: 'center',
            width: '100%',
            justifyContent: 'center',
          }}
        >
          <Card>
            <CardContent className="p-8 items-center">
              <MaterialIcons name="lock" size={48} color={COLORS.primary} style={{ marginBottom: 16 }} />
              <Text className="text-2xl font-bold text-center mb-3" style={{ color: COLORS.text.primary }}>
                {t('plans.notAvailableTitle')}
              </Text>
              <Text className="text-base text-center leading-6" style={{ color: COLORS.text.secondary }}>
                {t('plans.notAvailableMessage')}
              </Text>
            </CardContent>
          </Card>
        </View>
      </SafeAreaView>
    );
  }

  const getIntervalDisplayPrice = (plan: Plan, cycle: BillingCycle): string => {
    if (isB2C && !pricingReady) return '—';
    const iv = plan.intervals.find((i) => i.cycle === cycle);
    if (!iv || iv.cents === 0) return '—';
    return formatCents(iv.cents, plan.currency);
  };

  /** For multi-month intervals: show per-month effective price */
  const getPerMonthPrice = (plan: Plan, cycle: BillingCycle): string | null => {
    const iv = plan.intervals.find((i) => i.cycle === cycle);
    if (!iv || iv.months <= 1 || iv.cents === 0) return null;
    const perMonth = Math.round(iv.cents / iv.months);
    return formatCents(perMonth, plan.currency);
  };

  // Pastel accent for each tier's icon block (matches the redesign palette).
  const PLAN_ICON: Record<string, { name: keyof typeof MaterialIcons.glyphMap; bg: string; fg: string }> = {
    tier1: { name: 'eco', bg: '#F1F5F9', fg: '#64748B' },
    tier2: { name: 'trending-up', bg: '#DBE7FF', fg: COLORS.primary },
    tier3: { name: 'workspace-premium', bg: '#E0E7FF', fg: COLORS.primary },
  };

  const CYCLE_LABEL: Record<BillingCycle, string> = {
    '1mo': t('plans.cycle1mo') || '1 month',
    '3mo': t('plans.cycle3mo') || '3 months',
    '6mo': t('plans.cycle6mo') || '6 months',
  };

  const PlanCard: React.FC<{ plan: Plan }> = ({ plan }) => {
    const isCurrentlySelected = selectedPlan === plan.id;
    const emphasis = planCardEmphasis(plan.id, badgeTier, preselectedTier);
    const mentorNote = isB2C ? mentorPlanNote(plan.id, badgeTier) : null;
    const icon = PLAN_ICON[plan.id] ?? PLAN_ICON.tier1;
    const ctaLabel = isCurrentlySelected
      ? t('plans.processing')
      : isPendingCancellation
        ? t('plans.changePlan')
        : useCardTrialCheckout
          ? t('plans.startTrial')
          : t('plans.continueToCheckout');

    const activeCycle = resolveBillingCycleForPlan(plan, selectedIntervals[plan.id]);
    const displayPrice = getIntervalDisplayPrice(plan, activeCycle);
    const perMonthPrice = getPerMonthPrice(plan, activeCycle);
    const activeInterval = plan.intervals.find((iv) => iv.cycle === activeCycle);
    const isMultiMonth = (activeInterval?.months ?? 1) > 1;
    // Checkout stays disabled until a concrete amount and period are shown.
    const priceKnown = !isB2C || (pricingReady && displayPrice !== '—');

    return (
      <View
        testID={`plan_card_${plan.id}`}
        style={{
          flex: stackPlans ? undefined : 1,
          width: stackPlans ? '100%' : undefined,
          maxWidth: stackPlans ? '100%' : 300,
          minWidth: stackPlans ? undefined : 240,
          paddingTop: emphasis.recommendedBadge ? 14 : 0,
        }}
      >
        <Card
          style={{
            borderWidth: emphasis.highlighted ? 2 : 1,
            borderColor: emphasis.highlighted ? COLORS.primary : COLORS.border,
            backgroundColor: emphasis.highlighted ? `${COLORS.primary}08` : COLORS.background,
            borderRadius: 20,
            overflow: 'visible',
          }}
        >
          {emphasis.recommendedBadge && (
            <View
              testID={`plan_recommended_badge_${plan.id}`}
              style={{
                position: 'absolute',
                top: -14,
                alignSelf: 'center',
                left: 0,
                right: 0,
                alignItems: 'center',
                zIndex: 10,
              }}
              pointerEvents="none"
            >
              <View
                className="flex-row items-center px-3 py-1 rounded-full"
                style={{ backgroundColor: COLORS.primary }}
              >
                <MaterialIcons name="star" size={12} color="white" />
                <Text className="text-xs font-semibold text-white ml-1">{t('plans.mostPopular')}</Text>
              </View>
            </View>
          )}

          <CardContent className="p-6" style={{ gap: 18 }}>
            {emphasis.preselectedChip ? (
              <View className="items-center">
                <View
                  className="px-3 py-1 rounded-full"
                  style={{ backgroundColor: `${COLORS.primary}14` }}
                >
                  <Text className="text-xs font-semibold" style={{ color: COLORS.primary }}>
                    {t('plans.preselectedBadge')}
                  </Text>
                </View>
              </View>
            ) : null}
            <View className="items-center" style={{ gap: 10 }}>
              <View
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 28,
                  backgroundColor: icon.bg,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MaterialIcons name={icon.name} size={28} color={icon.fg} />
              </View>
              <Text className="text-xl font-bold text-center" style={{ color: COLORS.text.primary }}>
                {plan.name}
              </Text>
              {plan.tagline ? (
                <Text
                  className="text-xs text-center leading-5"
                  style={{ color: COLORS.text.secondary, maxWidth: 260 }}
                >
                  {plan.tagline}
                </Text>
              ) : null}
            </View>

            {/* Per-tier cycle selector — only shown if tier has multiple intervals */}
            {plan.intervals.length > 1 && (
              <View className="flex-row rounded-full p-1" style={{ backgroundColor: '#F1F5F9' }}>
                {plan.intervals.map((iv) => {
                  const isActive = iv.cycle === activeCycle;
                  return (
                    <TouchableOpacity
                      key={iv.cycle}
                      className="flex-1 items-center py-1.5 rounded-full"
                      style={{ backgroundColor: isActive ? COLORS.background : 'transparent' }}
                      onPress={() => {
                        setSelectedIntervals((prev) => ({ ...prev, [plan.id]: iv.cycle }));
                        trackAuthEvent('plan_billing_toggled', { plan: plan.id, billing_period: iv.cycle });
                      }}
                    >
                      <Text
                        className="text-xs font-semibold"
                        style={{ color: isActive ? COLORS.primary : COLORS.text.secondary }}
                      >
                        {CYCLE_LABEL[iv.cycle]}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}

            {/* Price display */}
            {isB2C && !pricingReady ? (
              <View testID={`plan_price_loading_${plan.id}`} className="items-center" style={{ minHeight: 40, justifyContent: 'center' }}>
                <Text className="text-sm" style={{ color: COLORS.text.muted }}>{t('planPricing.loading')}</Text>
              </View>
            ) : !priceKnown ? (
              <View testID={`plan_price_error_${plan.id}`} className="items-center" style={{ gap: 6 }}>
                <Text className="text-sm text-center" style={{ color: COLORS.error }}>{t('planPricing.error')}</Text>
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() => setPricingAttempt((value) => value + 1)}
                  style={{ minHeight: 36, justifyContent: 'center', paddingHorizontal: 12 }}
                >
                  <Text className="text-sm font-semibold" style={{ color: COLORS.primary }}>{t('planPricing.retry')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
            <View testID={`plan_price_${plan.id}`} className="items-center">
              <View className="flex-row items-baseline">
                <Text
                  className="text-3xl font-extrabold"
                  style={{ color: COLORS.text.primary }}
                >
                  {displayPrice}
                </Text>
                <Text className="text-sm ml-1" style={{ color: COLORS.text.muted }}>
                  {isMultiMonth
                    ? `/ ${activeInterval!.months} ${t('plans.months') || 'mo'}`
                    : `/ ${t('plans.month') || 'mo'}`}
                </Text>
              </View>
              {isMultiMonth && perMonthPrice ? (
                <Text className="text-xs mt-1" style={{ color: COLORS.text.muted }}>
                  ≈ {perMonthPrice} / {t('plans.month') || 'mo'}
                </Text>
              ) : null}
            </View>
            )}

            <View style={{ gap: 10 }}>
              {plan.features.map((feature, index) => (
                <View key={index} className="flex-row items-start">
                  <MaterialIcons
                    name="check-circle"
                    size={16}
                    color={COLORS.primary}
                    style={{ marginRight: 8, marginTop: 2 }}
                  />
                  <Text
                    className="flex-1 text-sm leading-5"
                    style={{ color: COLORS.text.primary }}
                  >
                    {feature.text}
                  </Text>
                </View>
              ))}
            </View>

            {mentorNote?.kind === 'onlyIn' ? (
              <View
                testID={`plan_mentor_only_${plan.id}`}
                style={{ gap: 8, borderRadius: 12, padding: 12, backgroundColor: `${COLORS.primary}0D` }}
              >
                <Text className="text-xs font-bold uppercase" style={{ color: COLORS.primary, letterSpacing: 0.4 }}>
                  {t('plans.mentorUpsell.onlyInTitle')}
                </Text>
                {mentorNote.keys.map((key) => (
                  <View key={key} className="flex-row items-start">
                    <MaterialIcons name="star" size={15} color={COLORS.primary} style={{ marginRight: 8, marginTop: 2 }} />
                    <Text className="flex-1 text-sm leading-5" style={{ color: COLORS.text.primary }}>
                      {t(`plans.mentorUpsell.${key}`)}
                    </Text>
                  </View>
                ))}
              </View>
            ) : mentorNote?.kind === 'menteesNotIncluded' ? (
              <View testID={`plan_mentees_not_included_${plan.id}`} className="flex-row items-start">
                <MaterialIcons name="remove-circle-outline" size={16} color={COLORS.text.muted} style={{ marginRight: 8, marginTop: 2 }} />
                <Text className="flex-1 text-sm leading-5" style={{ color: COLORS.text.muted }}>
                  {t('plans.mentorUpsell.menteesNotIncluded')}
                </Text>
              </View>
            ) : null}

            {/* Mandatory renewal-transparency copy, repeated here at the
                actual checkout CTA (also shown once on the FinalOfferBanner
                card above) — never let the fixed intro price stand alone
                without the real recurring price next to it. */}
            {finalOfferArmed && (plan.id === 'tier1' || plan.id === 'tier2') ? (
              <Text className="text-[11px] text-center" style={{ color: COLORS.text.muted }}>
                {t('billing.finalOffer.renewalNotice', { price: finalOfferFullPriceByTier[plan.id] || '' })}
              </Text>
            ) : null}

            <Button
              variant={emphasis.highlighted ? 'default' : 'outline'}
              size="default"
              onPress={() => handlePlanSelect(plan.id)}
              disabled={(isCurrentlySelected && loading) || !priceKnown}
              className="w-full"
              testID={`plan_cta_${plan.id}`}
            >
              <Text className={`font-semibold ${emphasis.highlighted ? 'text-white' : 'text-foreground'}`}>
                {ctaLabel}
              </Text>
            </Button>
          </CardContent>
        </Card>
      </View>
    );
  };

  // ─── Sidebar: "Your account is ready" reassurance card ─────────────
  const ReadyCard: React.FC = () => {
    const items = [
      t('plans.ready.goalSaved'),
      t('plans.ready.languageSet'),
      t('plans.ready.slotOpened'),
    ];
    return (
      <View
        style={{
          flex: stackSidebar ? undefined : 1,
          width: stackSidebar ? '100%' : undefined,
          maxWidth: stackSidebar ? '100%' : 280,
          minWidth: stackSidebar ? undefined : 240,
        }}
      >
        <Card
          style={{
            borderWidth: 1,
            borderColor: '#FEE9C2',
            backgroundColor: '#FFFBEE',
            borderRadius: 20,
          }}
        >
          <CardContent className="p-6" style={{ gap: 14 }}>
            <View className="items-center" style={{ gap: 10 }}>
              <View
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 28,
                  backgroundColor: '#FEE9C2',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MaterialIcons name="person" size={28} color="#B45309" />
              </View>
              <Text className="text-base font-bold text-center" style={{ color: COLORS.text.primary }}>
                {t('plans.ready.title')}
              </Text>
            </View>

            <View style={{ gap: 10 }}>
              {items.map((item, index) => (
                <View key={index} className="flex-row items-start">
                  <MaterialIcons
                    name="check-circle"
                    size={16}
                    color={COLORS.secondary}
                    style={{ marginRight: 8, marginTop: 2 }}
                  />
                  <Text className="flex-1 text-xs leading-5" style={{ color: COLORS.text.primary }}>
                    {item}
                  </Text>
                </View>
              ))}
            </View>

            <Text className="text-xs leading-5" style={{ color: COLORS.text.secondary }}>
              {t('plans.ready.hint')}
            </Text>
          </CardContent>
        </Card>
      </View>
    );
  };

  // ─── Header chips: small context pills under the progress bar ──────
  const goalLevelLabel = (() => {
    const raw = (intakeSummary?.primary_goal || '').toUpperCase();
    if (raw === 'ACC' || raw === 'PCC' || raw === 'MCC') return raw;
    return null;
  })();
  const languageLabel = language === 'tr' ? 'Türkçe' : 'English';
  const chips: Array<{ icon: keyof typeof MaterialIcons.glyphMap; label: string }> = [
    ...(goalLevelLabel
      ? [{ icon: 'gps-fixed' as const, label: t('plans.chips.icfGoal', { level: goalLevelLabel }) }]
      : []),
    { icon: 'language', label: t('plans.chips.language', { language: languageLabel }) },
    { icon: 'graphic-eq', label: t('plans.chips.usage') },
  ];

  // ─── Trust strip (3 items at the bottom of the screen) ─────────────
  const trustItems: Array<{ icon: keyof typeof MaterialIcons.glyphMap; bg: string; fg: string; title: string; sub: string }> = [
    {
      icon: 'lock',
      bg: '#DBE7FF',
      fg: COLORS.primary,
      title: t('plans.trust.secureTitle'),
      sub: t('plans.trust.secureSub'),
    },
    {
      icon: 'verified-user',
      bg: '#DBE7FF',
      fg: COLORS.primary,
      title: t('plans.trust.icfTitle'),
      sub: t('plans.trust.icfSub'),
    },
    {
      icon: 'autorenew',
      bg: '#DBE7FF',
      fg: COLORS.primary,
      title: useCardTrialCheckout ? t('plans.trust.noChargeTitle') : t('plans.trust.payTodayTitle'),
      sub: useCardTrialCheckout ? t('plans.trust.noChargeSub') : t('plans.trust.payTodaySub'),
    },
  ];

  return (
    <SafeAreaView className="flex-1 bg-surface">
      <AppHeaderBar />
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: isMobile ? 16 : 32,
          paddingTop: 24,
          paddingBottom: Math.max(insets.bottom + 40, 60),
          maxWidth: 1280,
          alignSelf: 'center',
          width: '100%',
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Step progress bar (Step 4 of 4) */}
        <View className="mb-5 flex-row items-center" style={{ gap: 12 }}>
          <View
            className="px-2 py-1 rounded-full"
            style={{ borderWidth: 1, borderColor: COLORS.primary }}
          >
            <Text className="text-xs font-semibold" style={{ color: COLORS.primary }}>4 / 4</Text>
          </View>
          <View className="flex-1 flex-row" style={{ gap: 6 }}>
            {[0, 1, 2, 3].map((i) => (
              <View
                key={i}
                className="flex-1 h-1 rounded-full"
                style={{ backgroundColor: COLORS.primary }}
              />
            ))}
          </View>
        </View>

        {/* Context chips */}
        {chips.length > 0 ? (
          <View className="flex-row flex-wrap justify-center mb-5" style={{ gap: 8 }}>
            {chips.map((chip, idx) => (
              <View
                key={idx}
                className="flex-row items-center px-3 py-1.5 rounded-full"
                style={{ backgroundColor: '#EEF2FF', borderWidth: 1, borderColor: '#DDE3FF' }}
              >
                <MaterialIcons name={chip.icon} size={14} color={COLORS.primary} />
                <Text className="text-xs font-medium ml-1.5" style={{ color: COLORS.text.primary }}>
                  {chip.label}
                </Text>
              </View>
            ))}
          </View>
        ) : null}

        {/* Title + subtitle */}
        <View className="items-center mb-5">
          <Text
            className="text-3xl font-extrabold text-center"
            style={{ color: COLORS.text.primary, lineHeight: 38 }}
          >
            {useCardTrialCheckout ? t('plans.selectionTitle') : t('plans.selectionTitlePaid')}
          </Text>
          <Text className="text-sm text-center mt-2" style={{ color: COLORS.text.secondary }}>
            {useCardTrialCheckout ? t('plans.selectionSubtitleLine1') : t('plans.selectionSubtitlePaidLine1')}
          </Text>
          <Text className="text-sm text-center" style={{ color: COLORS.text.secondary }}>
            {useCardTrialCheckout ? t('plans.selectionSubtitleLine2') : t('plans.selectionSubtitlePaidLine2')}
          </Text>
        </View>

        {/* Already-covered escape hatch — a user with active access must never
            be stranded on the paywall. */}
        {alreadyCovered ? (
          <View
            className="rounded-2xl px-5 py-4 mb-6"
            style={{
              backgroundColor: '#EEF2FF',
              borderWidth: 1,
              borderColor: '#C7D2FE',
              gap: 12,
            }}
          >
            <View className="flex-row items-center" style={{ gap: 12 }}>
              <MaterialIcons name="check-circle" size={22} color={COLORS.primary} />
              <Text className="text-base font-bold flex-1" style={{ color: COLORS.text.primary }}>
                {t('plans.alreadyCovered.title')}
              </Text>
            </View>
            <Button
              variant="default"
              size="default"
              onPress={() => {
                trackAuthEvent('plan_selection_already_covered_continue');
                router.replace(ROUTES.TABS_ROOT as any);
              }}
              accessibilityRole="button"
            >
              <Text className="text-white font-semibold">{t('plans.alreadyCovered.cta')}</Text>
            </Button>
          </View>
        ) : null}

        {/* Paywall-abandonment offer ladder — strictly one step visible at a
            time. Step 2 (final offer) only ever renders after step 1 was
            shown and resolved, so this order also matches the ladder's
            escalation; the two are never both eligible simultaneously. */}
        {finalOfferEligible || finalOfferArmed ? (
          <FinalOfferBanner
            armed={finalOfferArmed}
            fixedPriceLabel={finalOfferFixedPriceLabel}
            tiers={finalOfferTiers}
            onAccept={handleFinalOfferAccept}
            onDismiss={handleFinalOfferDismiss}
          />
        ) : comebackOfferEligible || comebackOfferArmed ? (
          <ComebackOfferBanner
            armed={comebackOfferArmed}
            onAccept={handleComebackOfferAccept}
            onDismiss={handleComebackOfferDismiss}
          />
        ) : null}

        {/* Interval selector is now per-tier, rendered inside each PlanCard */}

        {/* "No payment due today" banner — only shown while the user is
            still on/eligible for a card-backed trial. After the trial has
            been consumed, picking a plan means paying today, so we hide it. */}
        {showNoPaymentBanner ? (
          <View
            className="rounded-2xl px-5 py-4 mb-6 flex-row items-center"
            style={{
              backgroundColor: '#ECFDF5',
              borderWidth: 1,
              borderColor: '#A7F3D0',
              gap: 14,
            }}
          >
            <View
              style={{
                width: 44,
                height: 44,
                borderRadius: 22,
                backgroundColor: '#D1FAE5',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MaterialIcons name="lock-open" size={22} color="#047857" />
            </View>
            <View style={{ flex: 1 }}>
              <Text
                className="text-lg font-extrabold"
                style={{ color: '#065F46' }}
              >
                {t('plans.noPaymentBanner.headline')}
              </Text>
              <Text className="text-sm" style={{ color: '#047857' }}>
                {t('plans.noPaymentBanner.subline')}
              </Text>
            </View>
          </View>
        ) : trialExpired ? (
          // Post-trial paywall: explain why we're charging now.
          <View
            className="rounded-2xl px-5 py-4 mb-6 flex-row items-center"
            style={{
              backgroundColor: '#FEF3C7',
              borderWidth: 1,
              borderColor: '#FDE68A',
              gap: 14,
            }}
          >
            <View
              style={{
                width: 44,
                height: 44,
                borderRadius: 22,
                backgroundColor: '#FDE68A',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MaterialIcons name="schedule" size={22} color="#92400E" />
            </View>
            <View style={{ flex: 1 }}>
              <Text className="text-lg font-extrabold" style={{ color: '#78350F' }}>
                {t('plans.trialEndedBanner.headline')}
              </Text>
              <Text className="text-sm" style={{ color: '#92400E' }}>
                {t('plans.trialEndedBanner.subline')}
              </Text>
            </View>
          </View>
        ) : null}

        {/* VIS-ONB-4: the proof comes before the prices. */}
        <TouchableOpacity
          testID="plan_sample_report_link"
          onPress={() => setShowSampleReport(true)}
          accessibilityRole="button"
          accessibilityLabel={t('plans.sampleReportLink')}
          className="mb-6 rounded-2xl px-5 py-4 flex-row items-center"
          style={{ backgroundColor: COLORS.background, borderWidth: 1, borderColor: COLORS.border, gap: 14 }}
        >
          <MaterialIcons name="description" size={24} color={COLORS.primary} />
          <View style={{ flex: 1, gap: 2 }}>
            <Text className="text-base font-semibold" style={{ color: COLORS.primary }}>
              {t('plans.sampleReportLink')}
            </Text>
            <Text className="text-sm" style={{ color: COLORS.text.secondary }}>
              {t('planPricing.sampleHint')}
            </Text>
          </View>
          <MaterialIcons name="chevron-right" size={22} color={COLORS.primary} />
        </TouchableOpacity>

        {/* Plans + sidebar row */}
        <View
          className="mb-6"
          style={{
            flexDirection: stackPlans ? 'column' : 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: stackPlans ? 'stretch' : 'flex-start',
            gap: 16,
          }}
        >
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
          {!stackSidebar ? <ReadyCard /> : null}
        </View>
        {stackSidebar ? (
          <View className="mb-6 items-center">
            <ReadyCard />
          </View>
        ) : null}

        {/* Trust strip */}
        <View
          className="rounded-2xl px-5 py-4 mb-6"
          style={{
            backgroundColor: COLORS.background,
            borderWidth: 1,
            borderColor: COLORS.border,
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          {trustItems.map((item, idx) => (
            <View key={idx} className="flex-row items-center" style={{ flex: 1, gap: 12 }}>
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: item.bg,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MaterialIcons name={item.icon} size={20} color={item.fg} />
              </View>
              <View style={{ flex: 1 }}>
                <Text className="text-sm font-bold" style={{ color: COLORS.text.primary }}>
                  {item.title}
                </Text>
                <Text className="text-xs mt-0.5" style={{ color: COLORS.text.secondary }}>
                  {item.sub}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {/* Footer */}
        <View className="items-center pt-2">
          <Text
            className="text-xs text-center leading-5"
            style={{ color: COLORS.text.muted }}
          >
            {useCardTrialCheckout ? t('plans.autoContinue') : t('plans.paidCheckoutNotice')}
          </Text>
          {/* The one refund promise, worded as in the Terms (BIL-L22). */}
          <Text
            className="text-xs text-center leading-5 mt-1"
            style={{ color: COLORS.text.muted }}
          >
            {t('plans.moneyBackGuarantee')}
          </Text>
          <View className="flex-row mt-3" style={{ gap: 16 }}>
            <TouchableOpacity onPress={() => Linking.openURL('https://mentorcoaching.ai/terms')}>
              <Text className="text-xs underline" style={{ color: COLORS.text.muted }}>
                {t('plans.legalTerms')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => Linking.openURL('https://mentorcoaching.ai/privacy')}>
              <Text className="text-xs underline" style={{ color: COLORS.text.muted }}>
                {t('plans.legalPrivacy')}
              </Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity onPress={handleContactSales} className="mt-4">
            <Text className="text-xs" style={{ color: COLORS.primary }}>
              {t('plans.schools.contact')} →
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
      <EmbeddedCheckoutModal
        visible={checkoutVisible}
        planTier={checkoutPlan.tier}
        billingCycle={checkoutPlan.cycle}
        cardTrial={checkoutPlan.cardTrial}
        trialPeriodDays={CARD_TRIAL_DAYS}
        promo={checkoutPlan.promo}
        onSessionCreated={handleCheckoutSessionCreated}
        onConflictResolved={handleCheckoutConflictResolved}
        onComplete={handleCheckoutComplete}
        onClose={handleCheckoutClose}
      />
      <SampleReportViewer
        visible={showSampleReport}
        surface="plan_selection"
        primaryAction="continue"
        onClose={() => setShowSampleReport(false)}
        onPrimaryPress={() => setShowSampleReport(false)}
      />
    </SafeAreaView>
  );
}
